import { useState } from 'react'
import { useAuthStore } from '../store/authStore'

type Tab = 'profile' | 'account'

export function Settings() {
  const { user, signOut } = useAuthStore() as any
  const [activeTab, setActiveTab] = useState<Tab>('profile')
  const [name, setName] = useState(user?.name || '')
  const [saving, setSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const tabs: { id: Tab; label: string; icon: string; description: string }[] = [
    { id: 'profile', label: 'Profile', icon: 'person', description: 'Manage your personal information' },
    { id: 'account', label: 'Account', icon: 'shield_person', description: 'Security and account settings' },
  ]

  const handleSaveProfile = async () => {
    setSaving(true)
    setSaveSuccess(false)
    try {
      // This would call the API in a real implementation
      console.log('Saving profile:', { name })
      await new Promise(resolve => setTimeout(resolve, 600))
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } finally {
      setSaving(false)
    }
  }

  // Get user initials for avatar
  const getInitials = () => {
    const displayName = name || user?.email || 'U'
    return displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
  }

  return (
    <div className="max-w-4xl mx-auto flex flex-col gap-8">
      {/* Header with animated entrance */}
      <div className="flex flex-col gap-2 animate-fade-up stagger-1">
        <h1 className="text-3xl md:text-4xl font-serif font-semibold text-text-main leading-tight">
          Settings
        </h1>
        <p className="text-text-muted">
          Manage your account preferences and personal information
        </p>
      </div>

      {/* Settings Layout */}
      <div className="flex flex-col lg:flex-row lg:items-start gap-6 animate-fade-up stagger-2">
        {/* Sidebar Navigation - sticky on desktop */}
        <div className="lg:w-64 flex-shrink-0 lg:sticky lg:top-6">
          <nav className="bg-white rounded-2xl shadow-card border border-border-color overflow-hidden">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-start gap-4 p-4 text-left transition-all duration-300 relative group ${
                  index !== 0 ? 'border-t border-border' : ''
                } ${
                  activeTab === tab.id
                    ? 'bg-primary/5'
                    : 'hover:bg-surface-soft'
                }`}
              >
                {/* Active indicator */}
                <div
                  className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-b from-primary to-primary-dark opacity-100'
                      : 'opacity-0'
                  }`}
                />

                {/* Icon */}
                <div className={`size-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-surface-muted text-text-muted group-hover:bg-primary/10 group-hover:text-primary'
                }`}>
                  <span className="material-symbols-outlined text-xl">{tab.icon}</span>
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className={`font-medium transition-colors ${
                    activeTab === tab.id ? 'text-primary-dark' : 'text-text-main'
                  }`}>
                    {tab.label}
                  </div>
                  <div className="text-xs text-text-muted mt-0.5 truncate">
                    {tab.description}
                  </div>
                </div>

                {/* Arrow */}
                <span className={`material-symbols-outlined text-lg transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'text-primary opacity-100 translate-x-0'
                    : 'text-text-light opacity-0 -translate-x-2 group-hover:opacity-50 group-hover:translate-x-0'
                }`}>
                  chevron_right
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content - fixed width container */}
        <div className="flex-1 min-w-0">
          {activeTab === 'profile' && (
            <div className="bg-white rounded-2xl shadow-card border border-border-color overflow-hidden w-full">
              {/* Profile Header */}
              <div className="relative h-24 bg-gradient-to-br from-primary/20 via-secondary/20 to-accent overflow-hidden">
                {/* Decorative pattern */}
                <div className="absolute inset-0 opacity-30">
                  <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <defs>
                      <pattern id="settings-grid" width="10" height="10" patternUnits="userSpaceOnUse">
                        <circle cx="1" cy="1" r="0.5" fill="currentColor" className="text-primary/40"/>
                      </pattern>
                    </defs>
                    <rect width="100" height="100" fill="url(#settings-grid)"/>
                  </svg>
                </div>
              </div>

              {/* Avatar */}
              <div className="px-6 -mt-10 relative z-10">
                <div className="size-20 rounded-2xl bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center text-white text-2xl font-serif font-semibold shadow-lg border-4 border-white">
                  {getInitials()}
                </div>
              </div>

              {/* Form Content */}
              <div className="p-6 pt-4 space-y-6">
                {/* Success Message */}
                {saveSuccess && (
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-green-50 border border-green-200 text-green-700">
                    <span className="material-symbols-outlined filled text-green-500">check_circle</span>
                    <span className="font-medium">Profile updated successfully</span>
                  </div>
                )}

                {/* Display Name Field */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-text-main">
                    <span className="material-symbols-outlined text-lg text-text-muted">badge</span>
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your display name"
                    className="input"
                  />
                  <p className="text-xs text-text-light">
                    This is how your name will appear across Kalypso AI
                  </p>
                </div>

                {/* Email Field */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-text-main">
                    <span className="material-symbols-outlined text-lg text-text-muted">mail</span>
                    Email Address
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="input bg-surface-muted text-text-muted pr-24"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-text-light bg-surface-soft px-2 py-1 rounded-md">
                      Verified
                    </span>
                  </div>
                  <p className="text-xs text-text-light flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">lock</span>
                    Email address cannot be changed for security reasons
                  </p>
                </div>

                {/* Divider */}
                <div className="border-t border-border pt-6">
                  <button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="btn btn-primary"
                  >
                    {saving ? (
                      <>
                        <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-lg">save</span>
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'account' && (
            <div className="space-y-6 w-full">
              {/* Session Card */}
              <div className="bg-white rounded-2xl shadow-card border border-border-color p-6">
                <div className="flex items-start gap-4">
                  <div className="size-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                    <span className="material-symbols-outlined text-2xl">devices</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif font-semibold text-lg text-text-main">Current Session</h3>
                    <p className="text-sm text-text-muted mt-1">
                      You're currently signed in on this device. Sign out to secure your account.
                    </p>
                    <div className="flex items-center gap-2 mt-4">
                      <button
                        onClick={signOut}
                        className="btn btn-secondary"
                      >
                        <span className="material-symbols-outlined text-lg">logout</span>
                        Sign Out
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Danger Zone Card */}
              <div className="bg-white rounded-2xl shadow-card border border-red-200 overflow-hidden">
                {/* Danger Header */}
                <div className="px-6 py-4 bg-gradient-to-r from-red-50 to-red-100/50 border-b border-red-200">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-red-500">warning</span>
                    <h3 className="font-serif font-semibold text-lg text-red-700">Danger Zone</h3>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="size-12 rounded-xl bg-red-100 flex items-center justify-center text-red-500 flex-shrink-0">
                      <span className="material-symbols-outlined text-2xl">delete_forever</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-text-main">Delete Account</h4>
                      <p className="text-sm text-text-muted mt-1">
                        Permanently delete your account and all associated data. This action cannot be undone.
                      </p>
                      <ul className="mt-3 space-y-1.5 text-sm text-text-muted">
                        <li className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-sm text-red-400">close</span>
                          All your projects will be deleted
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-sm text-red-400">close</span>
                          All generated photos will be removed
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-sm text-red-400">close</span>
                          Unused credits will be forfeited
                        </li>
                      </ul>
                      <button className="btn mt-4 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200">
                        <span className="material-symbols-outlined text-lg">delete</span>
                        Delete My Account
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
